import { createRef, Fragment, useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import useTheme from "hooks/useTheme";
import { useDispatch, useSelector } from "react-redux";

import { getAccountInfoById, getAllAccountsByUser, getAccountAmountById } from "actions/accounts";

import Balances from "views/components/dashboard/Balances";
import Transactions from "views/components/dashboard/Transactions";
import Nfts from "views/components/dashboard/Nfts";
import ASA from "views/components/dashboard/ASA";
import IDTokens from "views/components/dashboard/IDTokens";
import Contracts from "views/components/dashboard/Contracts";

import TAccountsIcon from "theme/icons/TAccounts";
import QRCodeIcon from "theme/icons/QRCode";
import { Link } from "react-router-dom";
import PayIcon from "theme/icons/Pay";
import BillIcon from "theme/icons/Bill";
import WalletConnectIcon from "theme/icons/WalletConnect";
import AlgoExplorerIcon from "theme/icons/AlgoExplorer";
import AccountCardQRModal from "theme/modals/dashboard/AccountCardQRModal";
import StyledTooltipWithoutArrow from "theme/tooltips/StyledTooltipWithoutArrow";
import { setCurrentSelectedAccount } from "actions/appPreferences";
import { getAllContactsByUser } from "actions/contacts";
import useRefresh from "hooks/useRefresh";
import { fetchingDataFromApi } from "actions/loaders";
import AlgorandLogoIcon from "theme/icons/AlgorandLogo";

const Dashboard = () => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor, baseCurrency } = useTheme();
	const { isRefresh } = useRefresh();
	const accountCards = createRef();

	const accounts = useSelector((state) => state.Accounts);
	const selectedAccount = useSelector((state) => state.AppPreferences?.["selected-account"]);
	const algoChain = useSelector((state) => state.AppPreferences?.algoChain);

	const [activeAccount, setActiveAccount] = useState(null);
	const [selectedCard, setSelectedCard] = useState(null);
	const [activeDashboardTab, setActiveDashboardTab] = useState(0);
	const [isMainAccountExists, setIsMainAccountExists] = useState(false);
	const [accountsAmount, setAccountsAmount] = useState(null);

	const [accountCardQRModalIsOpen, setAccountCardQRModalIsOpen] = useState(false);
	const [accountCardQRModalInfo, setAccountCardQRModalInfo] = useState(null);

	const DashboardTabs = [
		appLanguage?.balances,
		appLanguage?.transactions,
		appLanguage?.nfts,
		appLanguage?.asa,
		appLanguage?.idTokens,
		appLanguage?.contracts,
	];

	const SwitchDashboardTabs = (activeDashboardTab) => {
		switch (activeDashboardTab) {
			case 0:
				return <Balances />;
			case 1:
				return <Transactions />;
			case 2:
				return <Nfts />;
			case 3:
				return <ASA setActiveDashboardTab={setActiveDashboardTab} />;
			case 4:
				return <IDTokens />;
			case 5:
				return <Contracts />;
			default:
				return null;
		}
	};

	useEffect(() => {
		dispatch(getAllContactsByUser());
		dispatch(getAllAccountsByUser());
		setActiveAccount(null);
		setActiveDashboardTab(null);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (accounts?.length) {
			if (selectedAccount)
				for (let index in accounts) {
					if (selectedAccount === accounts[index]["account-address"] && !accounts[index]["hide-account"]) {
						setActiveAccount(accounts[index]);
						setSelectedCard(accountCards.current.children[index]);
						setIsMainAccountExists(true);
						setActiveDashboardTab(0);

						break;
					}
				}
			else
				for (let index in accounts) {
					if (!accounts[index]["hide-account"]) {
						setActiveAccount(accounts[index]);
						setSelectedCard(accountCards.current.children[index]);
						setIsMainAccountExists(true);
						setActiveDashboardTab(0);

						break;
					}
				}

			GetAmountOfAllAccounts();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accounts, algoChain, baseCurrency]);

	useEffect(() => {
		if (activeAccount) {
			dispatch(setCurrentSelectedAccount(activeAccount["account-address"]));
			dispatch(getAccountInfoById(activeAccount["account-address"], baseCurrency["currency-code"], algoChain));

			if (selectedCard) selectedCard.scrollIntoView({ behavior: "smooth" });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeAccount, algoChain]);

	const GetAmountOfAllAccounts = async () => {
		dispatch(fetchingDataFromApi(true));

		const amountsArray = [];
		setAccountsAmount(null);

		for (let account of accounts) {
			await getAccountAmountById(account["account-address"], baseCurrency["currency-code"], algoChain).then(
				(accountData) => {
					if (accountData?.address) amountsArray.push(accountData);
					else
						amountsArray.push({
							address: account["account-address"],
							amount: 0,
							"total-amount-without-pending-rewards-in-algorand": 0,
							"total-amount-without-pending-rewards-in-base-currency": 0,
						});
				}
			);
		}

		setAccountsAmount(amountsArray);

		dispatch(fetchingDataFromApi(false));
	};

	useEffect(() => {
		if (isRefresh) GetAmountOfAllAccounts();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefresh]);

	return accounts?.length ? (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="flex flex-col w-full items-center justify-center">
				<div className="flex w-full items-center justify-center">
					<div className="flex w-fit items-center justify-start overflow-x-auto space-x-5 pb-2" ref={accountCards}>
						{accounts?.map(
							(account, index) =>
								!account["hide-account"] && (
									<div
										className={`flex flex-col relative h-full border hover:shadow-lg smooth p-3 pb-2 rounded-xl cursor-pointer
									 ${themeColor?.border} ${
											activeAccount?.["account-address"] === account?.["account-address"] &&
											`bg-opacity-10 ${themeColor?.bg}`
										}`}
										key={index}
									>
										<div
											className={`flex flex-col w-28 h-28 space-y-4`}
											onClick={() => {
												setActiveAccount(account);
												setSelectedCard(accountCards.current.children[index]);
											}}
										>
											<div className={`flex w-full justify-between`}>
												{account["profile-image"] ? (
													<img
														className={`w-10 h-10 object-cover object-center rounded-lg`}
														src={account["profile-image"]}
														alt="temp"
													/>
												) : (
													<TAccountsIcon className={`w-10`} fill={themeColor?.hex} />
												)}
											</div>

											<div className="flex flex-col h-full justify-between space-y-1">
												<div className="items-center justify-start">
													<h3 className={`text-sm truncate ${themeColor?.text}`}>{account?.name}</h3>
												</div>
												{accountsAmount?.length ? (
													accountsAmount?.map(
														(accountAmount, index) =>
															accountAmount?.address === account["account-address"] && (
																<div className="flex flex-col w-full" key={index}>
																	<div className="flex w-full items-center justify-end space-x-1">
																		{
																			<p
																				className={`text-xs font-medium ${themeColor?.text}`}
																			>
																				{(accountAmount?.amount / 1000000).toLocaleString(
																					undefined,
																					{
																						maximumFractionDigits: 2,
																						minimumFractionDigits: 2,
																					}
																				)}
																			</p>
																		}
																		<div className="w-2.5">
																			<AlgorandLogoIcon fill={themeColor?.hex} />
																		</div>
																	</div>
																	<div className="flex w-full items-center justify-end space-x-1">
																		{
																			<p
																				className={`text-sm font-bold ${themeColor?.text}`}
																			>
																				{(baseCurrency["currency-code"] === "algo"
																					? accountAmount?.[
																							"total-amount-without-pending-rewards-in-algorand"
																					  ]
																					: accountAmount?.[
																							"total-amount-without-pending-rewards-in-base-currency"
																					  ]
																				).toLocaleString(undefined, {
																					maximumFractionDigits: 2,
																					minimumFractionDigits: 2,
																				})}
																			</p>
																		}
																		<div className="w-3">{baseCurrency?.icon}</div>
																	</div>
																</div>
															)
													)
												) : (
													<div className="flex flex-col w-full animate-pulse">
														<div className="flex w-full items-center justify-end space-x-1">
															<p className={`text-xs font-medium ${themeColor?.text}`}>~</p>
															<div className="w-2.5">
																<AlgorandLogoIcon fill={themeColor?.hex} />
															</div>
														</div>
														<div className="flex w-full items-center justify-end space-x-1">
															<p className={`text-sm font-bold ${themeColor?.text}`}>~</p>
															<div className="w-3">{baseCurrency?.icon}</div>
														</div>
													</div>
												)}
											</div>
										</div>

										<QRCodeIcon
											className={`w-8 absolute right-3 hover:shadow-lg`}
											fill={themeColor?.hex}
											onClick={() => {
												setAccountCardQRModalInfo(account);
												setAccountCardQRModalIsOpen(true);
											}}
										/>
									</div>
								)
						)}
					</div>
				</div>

				<div className="flex flex-col w-[70%] items-center justify-center space-y-2 pt-5">
					{isMainAccountExists ? (
						<div className="flex w-full items-center justify-center space-x-10">
							<div className="flex items-center space-x-2">
								<StyledTooltipWithoutArrow
									title={appLanguage?.daffiBuy}
									children={
										<Link to="/Buy">
											<PayIcon className="w-5" fill={themeColor?.hex} />
										</Link>
									}
								/>
								<StyledTooltipWithoutArrow
									title={appLanguage?.daffiPay}
									children={
										<Link to="/Pay">
											<BillIcon className="w-5" fill={themeColor?.hex} />
										</Link>
									}
								/>
								<StyledTooltipWithoutArrow
									title={appLanguage?.algoExplorer}
									children={
										<AlgoExplorerIcon
											className={`w-4 cursor-pointer`}
											fill={themeColor?.hex}
											onClick={() =>
												window.open(
													`https://algoexplorer.io/address/${activeAccount["account-address"]}`,
													"_blank"
												)
											}
										/>
									}
								/>
								<StyledTooltipWithoutArrow
									title={appLanguage?.walletConnect}
									children={
										<Link to="/WalletConnect">
											<WalletConnectIcon className="w-5" fill={themeColor?.hex} />
										</Link>
									}
								/>
							</div>

							<div className="flex items-center justify-center space-x-1">
								{DashboardTabs &&
									DashboardTabs.map((dashboardTab, index) => (
										<div
											onClick={() => setActiveDashboardTab(index)}
											className={`self-center py-1 px-2 text-sm cursor-pointer ${
												activeDashboardTab === index
													? `border-b-4 ${themeColor?.border} ${themeColor?.text}`
													: `${themeColor?.text}`
											}`}
											key={index}
										>
											{dashboardTab}
										</div>
									))}
							</div>
						</div>
					) : (
						<div className="flex w-full items-center justify-center">
							<div className="flex-col text-center">
								<h1 className={`text-3xl ${themeColor?.text}`}>{appLanguage?.NoAccountIsSetOnMain}</h1>
								<p className={`text-md ${themeColor?.text}`}>{appLanguage?.setAnAccountToShowOnMain}</p>
							</div>
						</div>
					)}
				</div>

				<div className="flex-1 w-[70%] h-full overflow-y-auto">{SwitchDashboardTabs(activeDashboardTab)}</div>

				<AccountCardQRModal
					isOpen={accountCardQRModalIsOpen}
					setIsOpen={setAccountCardQRModalIsOpen}
					modalInfo={accountCardQRModalInfo}
				/>
			</div>
		</Transition>
	) : (
		<div className="flex w-full items-center justify-center">
			<div className="flex-col text-center">
				<h1 className={`text-3xl ${themeColor?.text}`}>{appLanguage?.noAccountsFoundOnMain}</h1>
				<p className={`text-md ${themeColor?.text}`}>{appLanguage?.pleaseImportAnAccount}</p>
			</div>
		</div>
	);
};

export default Dashboard;
